import React, { useEffect, useRef, useState } from "react";
import { CheckCircle, AlertCircle, Info, Loader } from "lucide-react";
import { useHistory, useLocation } from "react-router";
import { useAuth } from "../contexts/Auth";
import { authService } from "../services/authService";
import { TableSort } from "../components/Dashboard/TableSort";
import { Alert, AlertTitle, AlertDescription } from "../components/ui/alert";
import CodeDrawer from "../components/Dashboard/Drawer";
import UserJourneyDrawer from "../components/Dashboard/UserJourneyDrawer";
import Welcome from "../components/Dashboard/Welcome";
import Search from "../components/Dashboard/Search";
import OccurencyDetails from "../components/Dashboard/OccurencyDetails";
import Filter from "../components/Dashboard/Filter";
import { matchesLogCriteria } from "../components/Dashboard/logUtils";
import moment from "moment";

// Build the dashboard URL for a given userId / optional sessionId so we have
// a single source of truth for shareable links.
export const buildDashboardPath = (userId, sessionId) => {
  if (!userId) return "/";
  const params = new URLSearchParams({ userId });
  if (sessionId) params.set("sessionId", sessionId);
  return `/?${params.toString()}`;
};

export function Dashboard() {
  const { user, signOut } = useAuth();
  const [opened, setOpened] = useState(false);
  const [userJourneyOpened, setUserJourneyOpened] = useState(false);
  const [jsonValue, setJsonValue] = useState({});
  const [logs, setLogs] = useState([]);
  const [value, setValue] = useState("");
  const [newData, setNewData] = useState([]);
  const [filterBy, setFilterBy] = useState("user_sessions");
  const [currentUserId, setCurrentUserId] = useState("");
  const [currentSessionId, setCurrentSessionId] = useState("");
  const [viewType, setViewType] = useState(""); // "user_sessions" or "session_logs"
  const [activeFilter, setActiveFilter] = useState("All");
  const [notification, setNotification] = useState(null); // { variant: "info"|"success"|"error"|"loading", title, message }

  const history = useHistory();
  const location = useLocation();

  // Track the last userId/sessionId pair we've already fetched so URL-driven
  // effects don't re-trigger requests on every render.
  const lastFetchedRef = useRef({ userId: "", sessionId: "" });

  // Fetch user sessions list
  async function fetchUserSessions(userId) {
    try {
      const sessions = await authService.fetchUserSessions(userId);

      // Convert sessions array to table format
      const sessionData = sessions.map((session) => ({
        session_id: session.sessionId,
        received: session.firstLogTimestamp,
        logCount: session.logCount,
        lastLogTimestamp: session.lastLogTimestamp,
      }));

      setNewData(sessionData);
      setLogs(sessionData);
      setCurrentUserId(userId);
      setCurrentSessionId("");
      setViewType("user_sessions");
      setFilterBy("user_sessions");
      setActiveFilter("All");

      setNotification({ variant: "success", title: "User sessions loaded", message: `Found ${sessions.length} sessions` });
      setTimeout(() => setNotification(null), 2000);
    } catch (error) {
      setNotification({ variant: "error", title: "Error loading sessions", message: error.message });
      setTimeout(() => setNotification(null), 3000);
    }
  }

  // Fetch session logs
  async function fetchSessionLogs(userId, sessionId) {
    try {
      const sessionData = await authService.fetchSessionLogs(userId, sessionId);
      // Process the session logs data (structure may vary)
      let processedData = [];
      if (Array.isArray(sessionData)) {
        processedData = sessionData;
      } else if (sessionData.logs) {
        processedData = sessionData.logs;
      } else {
        // If it's a single object, wrap it in array
        processedData = [sessionData];
      }
      // Filter out logs that its "category":"ui.lifecycle"
      const filteredData = processedData.filter(
        (item) => !item?.message?.includes("ui.lifecycle")
      );

      // Process logs in a single efficient pass: deduplicate, parse, filter HTTP, and stringify
      const finalLogs = [];
      const seenItems = new Set();

      for (const item of filteredData) {
        // Create unique key for deduplication (O(1) lookup)
        const uniqueKey = `${item?.message}|${item?.timestamp}`;
        if (seenItems.has(uniqueKey)) {
          continue; // Skip duplicates
        }
        seenItems.add(uniqueKey);

        try {
          // Parse message once
          const parsedMessage = JSON.parse(item?.message);

          // Filter out HTTP requests to api.bleap.finance
          if (
            parsedMessage?.category === "http" &&
            parsedMessage?.data?.url?.includes("api.bleap.finance")
          ) {
            continue; // Skip this item
          }

          // Add processed item
          finalLogs.push({
            message: JSON.stringify(parsedMessage),
            timestamp: item?.timestamp,
          });
        } catch (error) {
          // Handle invalid JSON gracefully - keep original if parsing fails
          console.warn("Failed to parse log message:", error);
          finalLogs.push({
            message: item?.message,
            timestamp: item?.timestamp,
          });
        }
      }

      setNewData(finalLogs);
      setLogs(finalLogs);
      setCurrentUserId(userId);
      setCurrentSessionId(sessionId);
      setViewType("session_logs");
      setFilterBy("session_logs");
      setActiveFilter("All");

      setNotification({ variant: "success", title: "Session logs loaded", message: `Loaded logs for session ${sessionId}` });
      setTimeout(() => setNotification(null), 2000);
    } catch (error) {
      setNotification({ variant: "error", title: "Error loading session logs", message: error.message });
      setTimeout(() => setNotification(null), 3000);
    }
  }

  // Sync the search input and data fetching to the URL query params so that
  // opening `/?userId=...&sessionId=...` in a new tab reproduces the state.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlUserId = params.get("userId") || "";
    const urlSessionId = params.get("sessionId") || "";

    // Keep the search box in sync with the URL
    if (urlUserId && urlSessionId) {
      setValue(`${urlSessionId}/${urlUserId}`);
    } else if (urlUserId) {
      setValue(urlUserId);
    }

    // Skip fetching if we've already loaded this exact combination
    if (
      lastFetchedRef.current.userId === urlUserId &&
      lastFetchedRef.current.sessionId === urlSessionId
    ) {
      return;
    }
    lastFetchedRef.current = { userId: urlUserId, sessionId: urlSessionId };

    if (!urlUserId) return;

    setNotification({ variant: "loading", title: "Loading your data", message: "Fetching sessions and logs." });

    if (urlSessionId) {
      fetchSessionLogs(urlUserId, urlSessionId);
    } else {
      fetchUserSessions(urlUserId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleKeyDown = (event) => {
    if (event.key !== "Enter") return;

    const input = value.trim();
    if (!input) return;

    // Input patterns:
    //   userId            → /?userId=...
    //   sessionId/userId  → /?userId=...&sessionId=...
    let targetPath;
    if (input.includes("/")) {
      const [sessionId, userId] = input.split("/");
      targetPath = buildDashboardPath(userId, sessionId);
    } else {
      targetPath = buildDashboardPath(input);
    }

    // Don't re-push the same URL (avoids redundant fetches)
    if (targetPath !== `${location.pathname}${location.search}`) {
      history.push(targetPath);
    }
  };

  const handleRefresh = () => {
    if (!currentUserId) return;

    setNotification({ variant: "loading", title: "Refreshing data", message: "Reloading the current view." });

    if (viewType === "session_logs" && currentSessionId) {
      // Refresh session logs
      fetchSessionLogs(currentUserId, currentSessionId);
    } else if (viewType === "user_sessions") {
      // Refresh user sessions
      fetchUserSessions(currentUserId);
    }
  };

  const userSteps = logs?.filter((item) =>
    item?.message?.includes("current route name")
  );

  const handleFilterPress = (type) => {
    setActiveFilter(type);

    if (type === "All") {
      setNewData(logs);
      return;
    }

    // Only filter if we're viewing session logs, not user sessions
    if (viewType !== "session_logs") {
      return;
    }

    // Map filter names to actual data properties
    const filterMappings = {
      Info: { level: "info" },
      Errors: { level: "error" },
      Warns: { level: "warn" },
      Screens: { category: "screen" },
      Events: { category: "event" },
      State: { category: "state" },
      xhr: { category: "http" },
      Exceptions: { level: "error" },
    };

    const filterCriteria = filterMappings[type];
    if (!filterCriteria) {
      return;
    }

    const filteredData = logs.filter((item) =>
      matchesLogCriteria(item, filterCriteria)
    );

    setNewData(filteredData);
  };

  // Handle session click from the table
  const handleSessionClick = (sessionData) => {
    if (viewType === "user_sessions" && currentUserId) {
      // User clicked on a session from the sessions list → update the URL so
      // the logs view is shareable / openable in a new tab. The URL effect
      // will handle the actual fetch.
      history.push(buildDashboardPath(currentUserId, sessionData.session_id));
    } else {
      // Show session details in drawer
      setJsonValue(sessionData);
      setOpened(true);
    }
  };

  async function handleSignOut() {
    await signOut();
    history.push("/login");
  }

  // Handle user journey drawer
  const handleUserJourneyOpen = () => {
    setUserJourneyOpened(true);
  };

  const duration = moment
    .duration(
      moment(logs?.[logs?.length - 1]?.timestamp).diff(
        moment(logs?.[0]?.timestamp)
      )
    )
    .humanize();

  return (
    <>
      <CodeDrawer
        opened={opened}
        jsonValue={jsonValue}
        handleOnClose={() => setOpened(false)}
      />
      <UserJourneyDrawer
        duration={duration}
        opened={userJourneyOpened}
        userSteps={userSteps}
        handleOnClose={() => setUserJourneyOpened(false)}
      />
      {notification && (
        <div className="dashboard-notification">
          <Alert>
            {notification.variant === "success" && <CheckCircle size={16} />}
            {notification.variant === "error" && <AlertCircle size={16} />}
            {notification.variant === "loading" && <Loader size={16} className="spin" />}
            {notification.variant === "info" && <Info size={16} />}
            <AlertTitle>{notification.title}</AlertTitle>
            <AlertDescription>{notification.message}</AlertDescription>
          </Alert>
        </div>
      )}
      <main className="dashboard-shell">
        <Welcome user={user} handleSignOut={handleSignOut} />
        <Search
          value={value}
          onChange={(event) => setValue(event.currentTarget.value)}
          handleKeyDown={handleKeyDown}
        />
        {logs && logs.length > 0 && (
          <>
            <OccurencyDetails
              handleRefresh={handleRefresh}
              onClick={() => setOpened(true)}
              data={logs}
              userSteps={userSteps}
              onUserStepsClick={handleUserJourneyOpen}
            />
            {viewType === "session_logs" && (
              <Filter onClick={handleFilterPress} active={activeFilter} />
            )}
          </>
        )}
        {newData.length > 0 && (
          <div className="dashboard-table-wrap">
            <TableSort
              filterBy={filterBy}
              data={newData}
              viewType={viewType}
              userId={currentUserId}
              onPress={handleSessionClick}
            />
          </div>
        )}
      </main>
    </>
  );
}
